import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { useDashboardStructure } from '@/features/dashboard/api/queries'
import {
  allMarketIds,
  parseSelection,
  toggleMarket,
  writeSelection,
  type FilterSelection,
} from './filterState'

/**
 * The dashboard filters, kept in the URL so a view can be shared. Both are
 * `undefined` until the structure (which says what is on offer) has loaded,
 * or if it has no filters.
 */
export const useDashboardFilters = () => {
  const { data: dashboard } = useDashboardStructure()
  const filters = dashboard?.filters
  const [params, setParams] = useSearchParams()

  const selection = useMemo(
    () => (filters ? parseSelection(params, filters) : undefined),
    [params, filters]
  )
  const allIds = useMemo(
    () => (filters ? allMarketIds(filters) : []),
    [filters]
  )

  const update = useCallback(
    (change: (current: FilterSelection) => FilterSelection) => {
      if (!filters) return
      setParams(
        current =>
          writeSelection(
            current,
            change(parseSelection(current, filters)),
            filters
          ),
        { replace: true }
      )
    },
    [filters, setParams]
  )

  const setPeriods = useCallback(
    (periods: number) => update(current => ({ ...current, periods })),
    [update]
  )
  const toggle = useCallback(
    (id: string) =>
      update(current => ({
        ...current,
        markets: toggleMarket(current.markets, allIds, id),
      })),
    [update, allIds]
  )
  const clearMarkets = useCallback(
    () => update(current => ({ ...current, markets: allIds })),
    [update, allIds]
  )

  const isMarketFiltered =
    selection !== undefined && selection.markets.length < allIds.length

  return {
    periods: selection?.periods,
    /** The selected market ids; `undefined` when every market is (no filter). */
    markets: isMarketFiltered ? selection.markets : undefined,
    /** Every market id, selected or not, for the chips to show. */
    selectedMarkets: selection?.markets ?? allIds,
    isMarketFiltered,
    setPeriods,
    toggleMarket: toggle,
    clearMarkets,
  }
}
